// =============================================================================
// PPIProvas — monta a prova SORTEANDO do banco de questões
// =============================================================================
// Fluxo: escolhe as ÁREAS e quantas questões de cada (opcionalmente por NÍVEL)
// → [Sortear] → revisa a prova montada (pode trocar uma questão ou re-sortear
// tudo) → [Publicar].
//
// Depois de aplicada, a seleção é CONGELADA (não dá para re-sortear — mudaria a
// prova debaixo de quem já fez). Mas o RH ainda pode **anular uma questão** e
// **corrigir o gabarito** — as duas mexem no ranking na hora, e as duas são
// POR PROVA (não tocam a questão no banco nem as outras provas).

import React, { useState, useEffect, useCallback } from 'react';
import { Plus, ArrowLeft, Trash2, Shuffle, RefreshCw, CheckCircle2, Ban, RotateCcw, Archive, History, AlertTriangle, Replace, X } from 'lucide-react';
import { CORES, BadgeStatus, Botao, estilos } from './ppiUI';

const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
});

// Excluir prova é SÓ Admin (2026-07-15). A trava real é no backend; aqui só
// escondemos o botão para os demais.
const ehAdmin = () => {
    try {
        return (JSON.parse(localStorage.getItem('user') || '{}')).perfil === 'Admin';
    } catch (_) {
        return false;
    }
};

const TOM_STATUS = { Rascunho: 'rascunho', Publicado: 'ativo', Arquivado: 'neutro' };
// "Avançado" (2026-07-23): algumas trilhas têm esse 4º nível. O nível é texto
// livre da planilha e o sorteio filtra por NIVEL = ?, então basta oferecê-lo aqui.
const NIVEIS = ['Fácil', 'Médio', 'Difícil', 'Avançado'];
const TOM_NIVEL = { 'Fácil': 'aprovado', 'Médio': 'emProcesso', 'Difícil': 'reprovado', 'Avançado': 'inscrito' };

// O mix inicial sugerido — é o exemplo que a Julia deu (15% difícil / 35% médio /
// 50% fácil). Editável; o que vale é a PROPORÇÃO, então não precisa somar 100.
// "Avançado" entra com 0% para não mudar as trilhas que não o usam.
const MIX_PADRAO = [
    { nivel: 'Difícil', percentual: 15 },
    { nivel: 'Médio', percentual: 35 },
    { nivel: 'Fácil', percentual: 50 },
    { nivel: 'Avançado', percentual: 0 },
];

// O código da prova (PRV-2026-001): monoespaçado, para conferir dígito a dígito.
const estiloCodigo = {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '12px', fontWeight: 800, color: CORES.primaria, letterSpacing: '0.5px',
};

function PPIProvas() {
    const [cargos, setCargos] = useState([]);
    const [lista, setLista] = useState([]);
    const [aberta, setAberta] = useState(null);
    const [erro, setErro] = useState('');

    const carregar = useCallback(async () => {
        try {
            const r = await fetch('/api/treinamento/ppi/prova/provas', { headers: authHeaders() });
            if (!r.ok) throw new Error('Erro ao carregar as provas.');
            setLista(await r.json());
        } catch (e) { setErro(e.message); }
    }, []);

    useEffect(() => {
        carregar();
        (async () => {
            const r = await fetch('/api/treinamento/ppi/prova/cargos', { headers: authHeaders() });
            if (r.ok) setCargos(await r.json());
        })();
    }, [carregar]);

    // A prova NASCE amarrada a uma trilha — é o que impede o sorteio de puxar
    // questão de outro cargo.
    const nova = async () => {
        if (!cargos.length) { setErro('Cadastre um cargo do PPI (aba Banco de questões) antes de criar a prova.'); return; }
        const opcoes = cargos.map((c, i) => `${i + 1}) ${c.NOME} (${c.QTD_QUESTOES} questões)`).join('\n');
        const escolha = window.prompt(`Para qual cargo do PPI é esta prova?\n\n${opcoes}\n\nDigite o número:`);
        const cargo = cargos[Number(escolha) - 1];
        if (!cargo) return;
        const nome = window.prompt('Nome da prova (ex.: "Treinador · Q3/2026"):');
        if (!nome?.trim()) return;
        try {
            const r = await fetch('/api/treinamento/ppi/prova/provas', {
                method: 'POST', headers: authHeaders(), body: JSON.stringify({ nome, idCargo: cargo.ID }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message);
            setAberta(d.id);
        } catch (e) { setErro(e.message); }
    };

    if (aberta) return <MontarProva id={aberta} onVoltar={() => { setAberta(null); carregar(); }} />;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                    <h3 style={estilos.tituloSecao}>Provas</h3>
                    <p style={estilos.subtituloSecao}>Cada prova é um sorteio do banco de questões.</p>
                </div>
                <Botao icone={Plus} onClick={nova}>Nova prova</Botao>
            </div>

            {erro && <p style={{ color: '#be123c' }}>{erro}</p>}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                {lista.map((p) => (
                    <div key={p.ID} onClick={() => setAberta(p.ID)}
                        style={{ backgroundColor: '#fff', border: `1px solid ${CORES.borda}`, borderRadius: '14px', padding: '20px', cursor: 'pointer' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '10px' }}>
                            <div>
                                {p.CODIGO && <div style={estiloCodigo}>{p.CODIGO}</div>}
                                <h4 style={{ fontSize: '16px', fontWeight: 800, color: CORES.texto, margin: '2px 0 0 0' }}>{p.NOME}</h4>
                            </div>
                            <BadgeStatus tom={TOM_STATUS[p.STATUS] || 'neutro'}>{p.STATUS}</BadgeStatus>
                        </div>
                        <p style={{ fontSize: '12px', fontWeight: 700, color: CORES.primaria, margin: '0 0 4px 0' }}>{p.CARGO_PPI}</p>
                        <p style={{ fontSize: '13px', color: CORES.textoFraco, margin: 0 }}>
                            {p.QTD_QUESTOES} questão(ões) · usada em {p.QTD_VAGAS} vaga(s)
                        </p>
                    </div>
                ))}
                {!lista.length && <p style={{ color: CORES.textoFraco }}>Nenhuma prova ainda.</p>}
            </div>
        </div>
    );
}

// ─── Montagem / revisão de uma prova ─────────────────────────────────────────
function MontarProva({ id, onVoltar }) {
    const [prova, setProva] = useState(null);
    const [config, setConfig] = useState([]);      // [{ idArea, nivel, quantidade }]
    const [areasProva, setAreasProva] = useState([]);  // a prova montada
    const [banco, setBanco] = useState([]);        // estoque do banco (por área/nível)
    const [podeSortear, setPodeSortear] = useState(true);
    const [usada, setUsada] = useState(false);
    const [erro, setErro] = useState('');
    const [msg, setMsg] = useState('');
    const [avisos, setAvisos] = useState([]);
    const [auditoria, setAuditoria] = useState(null);
    // MODO de montagem: 'manual' (quantidade por área — o de sempre) ou 'mix'
    // (total + percentual por nível; o sistema reparte entre as áreas).
    const [modo, setModo] = useState('manual');
    const [mix, setMix] = useState({ total: 40, niveis: MIX_PADRAO, idsAreas: [] });
    // Troca de questão ESCOLHENDO qual entra: guarda a linha da prova sendo trocada.
    const [trocando, setTrocando] = useState(null);

    const carregar = useCallback(async () => {
        setErro(''); setMsg('');
        try {
            const rp = await fetch(`/api/treinamento/ppi/prova/provas/${id}`, { headers: authHeaders() });
            if (!rp.ok) throw new Error('Erro ao carregar a prova.');
            const d = await rp.json();
            // O banco mostrado na montagem é SÓ o do cargo desta prova.
            const rb = await fetch(`/api/treinamento/ppi/prova/areas?cargo=${d.prova.ID_CARGO}`, { headers: authHeaders() });
            setProva(d.prova);
            setConfig(d.config.map((c) => ({ idArea: c.ID_AREA, nivel: c.NIVEL || '', quantidade: c.QUANTIDADE })));
            setAreasProva(d.areas);
            setPodeSortear(d.podeSortear);
            setUsada(d.usada);
            if (rb.ok) setBanco(await rb.json());
        } catch (e) { setErro(e.message); }
    }, [id]);

    useEffect(() => { carregar(); }, [carregar]);

    // Salva o PEDIDO do sorteio. No modo mix, o backend traduz total+percentuais em
    // slots (área × nível × quantidade) e grava na MESMA tabela — o sorteio nem sabe
    // que o mix existe. É o que mantém um caminho só para o sorteio errar.
    const salvarConfig = async () => {
        const corpo = {
            nome: prova.NOME,
            evitarRepetidas: prova.EVITAR_REPETIDAS === 1,
            ...(modo === 'mix'
                ? { mix: { total: Number(mix.total) || 0, niveis: mix.niveis, idsAreas: mix.idsAreas } }
                : { config: config.filter((c) => c.idArea && c.quantidade > 0) }),
        };
        const r = await fetch(`/api/treinamento/ppi/prova/provas/${id}`, {
            method: 'PUT', headers: authHeaders(), body: JSON.stringify(corpo),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.message || 'Erro ao salvar.');
        if (d.avisos?.length) setAvisos(d.avisos);
        return d;
    };

    const acao = async (rota, corpo, metodo = 'POST', confirmar) => {
        if (confirmar && !window.confirm(confirmar)) return;
        setErro(''); setMsg(''); setAvisos([]);
        try {
            const r = await fetch(`/api/treinamento/ppi/prova/${rota}`, {
                method: metodo, headers: authHeaders(), body: corpo ? JSON.stringify(corpo) : undefined,
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message);
            setMsg(d.message);
            if (d.avisos?.length) setAvisos(d.avisos);
            carregar();
        } catch (e) { setErro(e.message); }
    };

    const sortear = async () => {
        if (areasProva.length && !window.confirm('Re-sortear a prova? As questões atuais serão substituídas.')) return;
        setErro(''); setMsg(''); setAvisos([]);
        try {
            // Salvar ANTES de sortear: é o pedido salvo que o sorteio lê. Se o salvar
            // falha (ex.: mix sem área), o erro tem de aparecer — antes ele era engolido
            // e o sorteio rodava com a configuração ANTIGA, em silêncio.
            const salvo = await salvarConfig();
            const r = await fetch(`/api/treinamento/ppi/prova/provas/${id}/sortear`, {
                method: 'POST', headers: authHeaders(),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message);
            setMsg(d.message);
            // Avisos do sorteio (estoque insuficiente) + os da repartição do mix.
            setAvisos([...(salvo.avisos || []), ...(d.avisos || [])]);
            carregar();
        } catch (e) { setErro(e.message); }
    };

    const verAuditoria = async () => {
        const r = await fetch(`/api/treinamento/ppi/prova/provas/${id}/auditoria`, { headers: authHeaders() });
        if (r.ok) setAuditoria(await r.json());
    };

    // Excluir a prova em definitivo (só Admin, e o backend recusa prova já
    // respondida — nesse caso o caminho é Arquivar). Volta para a lista ao apagar.
    const excluirProva = async () => {
        if (!window.confirm('Excluir esta prova em definitivo? Não dá para desfazer.\n\nSó é possível se ninguém a respondeu — o histórico é sempre preservado.')) return;
        setErro('');
        try {
            const r = await fetch(`/api/treinamento/ppi/prova/provas/${id}`, { method: 'DELETE', headers: authHeaders() });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message);
            onVoltar();
        } catch (e) { setErro(e.message); }
    };

    // Confirma a troca depois que o RH escolheu (ou deixou o sistema sortear).
    const confirmarTroca = async (idQuestaoNova) => {
        const alvo = trocando;
        setTrocando(null);
        await acao(`provas/questao/${alvo.id}/trocar`, idQuestaoNova ? { idQuestaoNova } : null, 'POST');
    };

    // Estoque disponível para um slot (área + nível) — mostra ANTES de sortear.
    const estoque = (idArea, nivel) => {
        const a = banco.find((x) => x.id === Number(idArea));
        if (!a) return null;
        if (!nivel) return a.ativas;
        return a.niveis?.[nivel]?.ativas ?? 0;
    };

    const setC = (i, campo, valor) => setConfig((cs) => cs.map((c, k) => k === i ? { ...c, [campo]: valor } : c));
    const addLinha = () => setConfig((cs) => [...cs, { idArea: '', nivel: '', quantidade: 5 }]);
    const rmLinha = (i) => setConfig((cs) => cs.filter((_, k) => k !== i));

    // O "total pedido" muda de fonte conforme o modo — é ele que habilita o Sortear.
    const totalPedido = modo === 'mix'
        ? (mix.idsAreas.length ? Number(mix.total) || 0 : 0)   // sem área, não há prova
        : config.reduce((s, c) => s + (Number(c.quantidade) || 0), 0);
    const totalMontado = areasProva.reduce((s, a) => s + a.questoes.length, 0);

    if (!prova) return <p style={{ color: CORES.textoFraco }}>Carregando…</p>;

    return (
        <div>
            <button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                <div>
                    {prova.CODIGO && <div style={{ ...estiloCodigo, fontSize: '13px' }}>{prova.CODIGO}</div>}
                    <h3 style={{ ...estilos.tituloSecao, margin: '2px 0 0 0' }}>{prova.NOME}</h3>
                    <p style={{ fontSize: '13px', fontWeight: 700, color: CORES.primaria, margin: '4px 0 0 0' }}>{prova.CARGO_PPI}</p>
                    <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                        <BadgeStatus tom={TOM_STATUS[prova.STATUS] || 'neutro'}>{prova.STATUS}</BadgeStatus>
                        {usada && <BadgeStatus tom="emProcesso">já respondida</BadgeStatus>}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {prova.STATUS === 'Rascunho' && totalMontado > 0 && (
                        <Botao variant="sucesso" icone={CheckCircle2} onClick={() => acao(`provas/${id}/publicar`)}>Publicar</Botao>
                    )}
                    <Botao variant="secundario" icone={History} onClick={verAuditoria}>Auditoria</Botao>
                    {prova.STATUS !== 'Arquivado' && (
                        <Botao variant="perigo" icone={Archive} onClick={() => acao(`provas/${id}/arquivar`, null, 'POST', 'Arquivar esta prova?')}>Arquivar</Botao>
                    )}
                    {/* Excluir: só Admin, e só se ninguém respondeu (prova usada é histórico). */}
                    {ehAdmin() && !usada && (
                        <Botao variant="perigo" icone={Trash2} onClick={excluirProva}>Excluir</Botao>
                    )}
                </div>
            </div>

            {usada && (
                <div style={{ backgroundColor: '#fffbeb', color: '#b45309', padding: '12px 14px', borderRadius: '10px', fontSize: '13px', margin: '14px 0' }}>
                    Esta prova <strong>já foi respondida</strong> — a seleção de questões está congelada. Mas você ainda pode
                    <strong> anular uma questão</strong> ou <strong>corrigir o gabarito</strong> abaixo: o ranking recalcula na hora, e
                    a mudança vale <strong>só nesta prova</strong> (o banco de questões não muda).
                </div>
            )}

            {erro && <p style={{ color: '#be123c' }}>{erro}</p>}
            {msg && <div style={{ backgroundColor: '#ecfdf5', color: '#047857', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', margin: '10px 0' }}>{msg}</div>}
            {avisos.length > 0 && (
                <div style={{ backgroundColor: '#fffbeb', borderRadius: '10px', padding: '12px 14px', margin: '10px 0' }}>
                    <p style={{ margin: '0 0 6px 0', color: '#b45309', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <AlertTriangle size={14} /> Avisos do sorteio:
                    </p>
                    <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', color: '#b45309' }}>
                        {avisos.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                </div>
            )}

            {auditoria && (
                <div style={{ ...estilos.tabelaWrap, margin: '12px 0', maxHeight: '200px', overflowY: 'auto' }}>
                    <table style={estilos.tabela}>
                        <thead><tr><th style={estilos.th}>Quando</th><th style={estilos.th}>Quem</th><th style={estilos.th}>O quê</th></tr></thead>
                        <tbody>
                            {auditoria.map((a) => (
                                <tr key={a.ID}>
                                    <td style={estilos.td}>{a.QUANDO}</td>
                                    <td style={estilos.td}>{a.USUARIO_NOME || '—'}</td>
                                    <td style={estilos.td}>{a.DETALHE}</td>
                                </tr>
                            ))}
                            {!auditoria.length && <tr><td style={estilos.td} colSpan={3}>Nada registrado.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ── CONFIGURAÇÃO DO SORTEIO ── */}
            {podeSortear && (
                <div style={{ border: `1px solid ${CORES.borda}`, borderRadius: '12px', padding: '18px', margin: '18px 0' }}>
                    <h4 style={{ fontSize: '16px', fontWeight: 800, color: CORES.texto, margin: '0 0 10px 0' }}>Como montar a prova</h4>

                    {/* Dois modos. O manual é o de sempre; o mix é a alternativa nova. */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
                        <AbaModo ativa={modo === 'manual'} onClick={() => setModo('manual')}>
                            Quantidade por área
                        </AbaModo>
                        <AbaModo ativa={modo === 'mix'} onClick={() => setModo('mix')}>
                            Percentual por nível
                        </AbaModo>
                    </div>

                    {modo === 'mix' ? (
                        <MontagemPorMix banco={banco} mix={mix} setMix={setMix} />
                    ) : (
                    <>
                    <p style={{ fontSize: '12px', color: CORES.textoFraco, margin: '0 0 14px 0' }}>
                        Escolha as áreas e quantas questões de cada. O nível é opcional — em branco, sorteia de qualquer nível.
                    </p>

                    {config.map((c, i) => {
                        const disp = c.idArea ? estoque(c.idArea, c.nivel) : null;
                        const faltando = disp !== null && Number(c.quantidade) > disp;
                        return (
                            <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginBottom: '8px', flexWrap: 'wrap' }}>
                                <div style={{ flex: 2, minWidth: '160px' }}>
                                    <label style={estilos.rotuloFiltro}>Área</label>
                                    <select style={estilos.input} value={c.idArea} onChange={(e) => setC(i, 'idArea', e.target.value)}>
                                        <option value="">Selecione…</option>
                                        {banco.map((a) => <option key={a.id} value={a.id}>{a.nome} ({a.ativas})</option>)}
                                    </select>
                                </div>
                                <div style={{ flex: 1, minWidth: '120px' }}>
                                    <label style={estilos.rotuloFiltro}>Nível</label>
                                    <select style={estilos.input} value={c.nivel} onChange={(e) => setC(i, 'nivel', e.target.value)}>
                                        <option value="">Qualquer</option>
                                        {NIVEIS.map((n) => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </div>
                                <div style={{ width: '110px' }}>
                                    <label style={estilos.rotuloFiltro}>Quantas</label>
                                    <input type="number" min="1" style={{ ...estilos.input, borderColor: faltando ? '#e11d48' : undefined }}
                                        value={c.quantidade} onChange={(e) => setC(i, 'quantidade', Number(e.target.value))} />
                                </div>
                                <div style={{ minWidth: '120px', paddingBottom: '10px', fontSize: '12px', color: faltando ? '#be123c' : CORES.textoFraco, fontWeight: faltando ? 700 : 400 }}>
                                    {disp !== null && (faltando ? `só há ${disp}!` : `${disp} disponíveis`)}
                                </div>
                                <button onClick={() => rmLinha(i)} style={{ ...btnIconePerigo, marginBottom: '4px' }}><Trash2 size={15} /></button>
                            </div>
                        );
                    })}
                    <Botao variant="secundario" icone={Plus} onClick={addLinha} style={{ marginTop: '4px' }}>Adicionar área</Botao>
                    </>
                    )}

                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '16px', flexWrap: 'wrap', borderTop: `1px solid ${CORES.borda}`, paddingTop: '14px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: CORES.textoSec, cursor: 'pointer' }}>
                            <input type="checkbox" checked={prova.EVITAR_REPETIDAS === 1}
                                onChange={(e) => setProva({ ...prova, EVITAR_REPETIDAS: e.target.checked ? 1 : 0 })} />
                            Evitar questões já aplicadas em provas anteriores
                        </label>
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                            <span style={{ alignSelf: 'center', fontSize: '13px', fontWeight: 700, color: CORES.texto, fontVariantNumeric: 'tabular-nums' }}>
                                Total: {totalPedido} questões
                            </span>
                            <Botao icone={areasProva.length ? RefreshCw : Shuffle} onClick={sortear} disabled={!totalPedido}>
                                {areasProva.length ? 'Re-sortear' : 'Sortear'}
                            </Botao>
                        </div>
                    </div>
                </div>
            )}

            {/* ── A PROVA MONTADA ── */}
            {areasProva.map((area) => (
                <div key={area.id} style={{ marginBottom: '20px' }}>
                    <h4 style={{ fontSize: '15px', fontWeight: 800, color: CORES.primaria, margin: '0 0 10px 0' }}>
                        {area.nome} <span style={{ color: CORES.textoFraco, fontWeight: 600 }}>· {area.questoes.length} questão(ões)</span>
                    </h4>

                    {area.questoes.map((q, i) => (
                        <div key={q.id} style={{
                            border: `1px solid ${q.anulada ? '#fecaca' : CORES.borda}`, borderRadius: '12px',
                            padding: '14px', marginBottom: '10px',
                            backgroundColor: q.anulada ? '#fff1f2' : '#fff', opacity: q.anulada ? 0.75 : 1,
                        }}>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                <span style={{ fontWeight: 900, color: CORES.primaria, minWidth: '22px' }}>{i + 1}.</span>
                                <p style={{ flex: 1, margin: 0, fontSize: '14px', color: CORES.texto, lineHeight: 1.5 }}>{q.texto}</p>
                                {/* O NÍVEL estava gravado e não aparecia: o RH revisava a prova
                                    sem saber se olhava uma fácil ou uma difícil. */}
                                {q.nivel && (
                                    <BadgeStatus tom={TOM_NIVEL[q.nivel] || 'neutro'}>{q.nivel}</BadgeStatus>
                                )}
                            </div>

                            <div style={{ paddingLeft: '32px', marginTop: '8px' }}>
                                {q.alternativas.map((a) => {
                                    const ehGabarito = a.id === q.idAlternativaCorreta;
                                    return (
                                        <div key={a.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
                                            <input
                                                type="radio" name={`g-${q.id}`} checked={ehGabarito}
                                                onChange={() => acao(`provas/questao/${q.id}/gabarito`, { idAlternativa: a.id }, 'PATCH',
                                                    usada ? 'Corrigir o gabarito?\n\nA prova já foi respondida — o ranking de todos vai recalcular (só nesta prova).' : null)}
                                                title="Marcar como o gabarito desta prova"
                                            />
                                            <span style={{
                                                fontSize: '13px', color: ehGabarito ? '#047857' : CORES.textoSec,
                                                fontWeight: ehGabarito ? 700 : 400,
                                                backgroundColor: ehGabarito ? '#ecfdf5' : 'transparent',
                                                padding: '2px 8px', borderRadius: '6px',
                                            }}>{a.texto}</span>
                                        </div>
                                    );
                                })}
                            </div>

                            <div style={{ display: 'flex', gap: '8px', paddingLeft: '32px', marginTop: '10px', flexWrap: 'wrap' }}>
                                {/* ANULAR só existe DEPOIS que a prova foi respondida.
                                    Na montagem ele não servia para nada — pior, atrapalhava:
                                    anular uma questão de um rascunho só queima um slot da prova
                                    (ela sai do cálculo, mas continua ocupando o lugar).
                                    Questão ruim numa prova que ninguém fez se resolve TROCANDO.
                                    Depois de aplicada, aí sim: anular é a ÚNICA forma de corrigir
                                    sem invalidar a prova inteira — e o ranking recalcula na hora. */}
                                {/* `|| q.anulada`: sem isso, uma questão anulada num RASCUNHO (de
                                    uma prova montada antes desta mudança) ficaria sem botão para
                                    Reativar — morta dentro da prova, ocupando um slot para sempre. */}
                                {(usada || q.anulada) && (
                                    <Botao
                                        variant={q.anulada ? 'secundario' : 'perigo'}
                                        icone={q.anulada ? RotateCcw : Ban}
                                        style={{ padding: '5px 10px', fontSize: '12px' }}
                                        onClick={() => acao(`provas/questao/${q.id}/anular`, { anulada: !q.anulada }, 'PATCH',
                                            q.anulada ? 'Reativar? Ela volta a contar para todos.' : 'Anular esta questão NESTA prova?\n\nEla sai do cálculo de TODOS os candidatos e o ranking recalcula na hora.')}
                                    >
                                        {q.anulada ? 'Reativar' : 'Anular'}
                                    </Botao>
                                )}
                                {podeSortear && (
                                    <Botao variant="secundario" icone={Replace} style={{ padding: '5px 10px', fontSize: '12px' }}
                                        onClick={() => setTrocando(q)}>
                                        Trocar questão
                                    </Botao>
                                )}
                                {q.anulada && <span style={{ fontSize: '12px', color: '#be123c', fontWeight: 700, alignSelf: 'center' }}>ANULADA nesta prova</span>}
                            </div>
                        </div>
                    ))}
                </div>
            ))}

            {!areasProva.length && (
                <p style={{ color: CORES.textoFraco }}>Configure as áreas acima e clique em <strong>Sortear</strong>.</p>
            )}

            {trocando && (
                <PainelTroca questao={trocando} onFechar={() => setTrocando(null)} onTrocar={confirmarTroca} />
            )}
        </div>
    );
}

// ─── Seletor de modo de montagem ─────────────────────────────────────────────
const AbaModo = ({ ativa, onClick, children }) => (
    <button onClick={onClick} style={{
        padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
        border: `1px solid ${ativa ? CORES.primaria : CORES.borda}`,
        backgroundColor: ativa ? CORES.primariaBg : '#fff',
        color: ativa ? CORES.primariaEscura : CORES.textoSec,
    }}>
        {children}
    </button>
);

// ─── Montagem por PERCENTUAL DE NÍVEL ────────────────────────────────────────
// O RH diz "40 questões, 15% difícil / 35% médio / 50% fácil" e escolhe as áreas;
// o servidor reparte o mix ENTRE as áreas (o percentual vale para a PROVA TODA).
// A prévia aqui é só orientativa — quem faz a conta que vale é o backend, com o
// método do maior resto (é ele que garante que a soma bate com o total pedido).
function MontagemPorMix({ banco, mix, setMix }) {
    const setNivel = (nivel, percentual) => setMix((m) => ({
        ...m,
        niveis: m.niveis.map((n) => (n.nivel === nivel ? { ...n, percentual: Number(percentual) || 0 } : n)),
    }));
    const alternarArea = (idArea) => setMix((m) => ({
        ...m,
        idsAreas: m.idsAreas.includes(idArea)
            ? m.idsAreas.filter((x) => x !== idArea)
            : [...m.idsAreas, idArea],
    }));

    const somaPct = mix.niveis.reduce((s, n) => s + (Number(n.percentual) || 0), 0);
    const total = Number(mix.total) || 0;

    // Prévia: quantas questões por nível. Arredondamento só para mostrar — o número
    // final vem do servidor. (Por isso o texto diz "≈".)
    const previa = mix.niveis.map((n) => ({
        ...n,
        qtd: somaPct > 0 ? Math.round((total * Number(n.percentual)) / somaPct) : 0,
    }));

    // Estoque de cada nível nas áreas escolhidas — é o que diz se o pedido cabe.
    const estoqueNivel = (nivel) => banco
        .filter((a) => mix.idsAreas.includes(a.id))
        .reduce((s, a) => s + (a.niveis?.[nivel]?.ativas || 0), 0);

    return (
        <div>
            <p style={{ fontSize: '12px', color: CORES.textoFraco, margin: '0 0 14px 0' }}>
                Diga o <strong>tamanho da prova</strong> e o <strong>mix de níveis</strong>; escolha as áreas e o sistema
                reparte. O percentual vale para a <strong>prova toda</strong>, não por área. Não precisa somar 100 —
                o que vale é a proporção.
            </p>

            <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '16px' }}>
                <div style={{ width: '150px' }}>
                    <label style={estilos.rotuloFiltro}>Total de questões</label>
                    <input type="number" min="1" style={estilos.input} value={mix.total}
                        onChange={(e) => setMix((m) => ({ ...m, total: e.target.value }))} />
                </div>
                {previa.map((n) => {
                    const disp = estoqueNivel(n.nivel);
                    const faltando = n.qtd > disp;
                    return (
                        <div key={n.nivel} style={{ width: '150px' }}>
                            <label style={estilos.rotuloFiltro}>{n.nivel} (%)</label>
                            <input type="number" min="0" max="100"
                                style={{ ...estilos.input, borderColor: faltando ? '#e11d48' : undefined }}
                                value={n.percentual} onChange={(e) => setNivel(n.nivel, e.target.value)} />
                            <div style={{
                                fontSize: '11px', marginTop: '4px', fontVariantNumeric: 'tabular-nums',
                                color: faltando ? '#be123c' : CORES.textoFraco, fontWeight: faltando ? 700 : 400,
                            }}>
                                ≈ {n.qtd} questão(ões)
                                {mix.idsAreas.length > 0 && (faltando ? ` · só há ${disp}!` : ` · ${disp} disponíveis`)}
                            </div>
                        </div>
                    );
                })}
            </div>

            <label style={estilos.rotuloFiltro}>Áreas da prova</label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {banco.map((a) => {
                    const marcada = mix.idsAreas.includes(a.id);
                    return (
                        <button key={a.id} onClick={() => alternarArea(a.id)} style={{
                            padding: '7px 14px', borderRadius: '999px', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                            border: `1px solid ${marcada ? CORES.primaria : CORES.borda}`,
                            backgroundColor: marcada ? CORES.primariaBg : '#fff',
                            color: marcada ? CORES.primariaEscura : CORES.textoSec,
                        }}>
                            {a.nome} ({a.ativas})
                        </button>
                    );
                })}
                {!banco.length && <p style={{ color: CORES.textoFraco, fontSize: '13px' }}>Banco vazio para esta trilha.</p>}
            </div>
            {!mix.idsAreas.length && (
                <p style={{ fontSize: '12px', color: '#b45309', marginTop: '10px' }}>
                    Escolha ao menos uma área — sem isso não há de onde sortear.
                </p>
            )}
        </div>
    );
}

// ─── Painel de TROCA: o RH ESCOLHE qual questão entra ────────────────────────
// Antes, "Trocar" sorteava a substituta e o RH só descobria qual depois. Agora ele
// vê as candidatas (mesma área e mesmo nível — trocar uma difícil por uma fácil
// desbalancearia a prova que ele mesmo pediu) e escolhe. O botão "sortear por mim"
// continua ali para quem não quer escolher.
function PainelTroca({ questao, onFechar, onTrocar }) {
    const [candidatas, setCandidatas] = useState(null);
    const [erro, setErro] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const r = await fetch(`/api/treinamento/ppi/prova/provas/questao/${questao.id}/candidatas`, { headers: authHeaders() });
                const d = await r.json();
                if (!r.ok) throw new Error(d.message);
                setCandidatas(d.candidatas);
            } catch (e) { setErro(e.message); }
        })();
    }, [questao.id]);

    return (
        <div style={overlay} onClick={onFechar}>
            <div style={painel} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                    <div>
                        <h4 style={{ fontSize: '17px', fontWeight: 800, color: CORES.texto, margin: 0 }}>Trocar questão</h4>
                        <p style={{ fontSize: '12px', color: CORES.textoFraco, margin: '4px 0 0 0' }}>
                            Sai: <em>{String(questao.texto).slice(0, 90)}…</em>
                        </p>
                    </div>
                    <button onClick={onFechar} style={{ background: 'none', border: 'none', cursor: 'pointer', color: CORES.textoFraco }}>
                        <X size={20} />
                    </button>
                </div>

                <p style={{ fontSize: '12px', color: CORES.textoSec, margin: '12px 0' }}>
                    Só aparecem questões da <strong>mesma área</strong>
                    {questao.nivel && <> e do <strong>mesmo nível ({questao.nivel})</strong></>}, que estejam ativas,
                    tenham gabarito e ainda não estejam nesta prova. As <strong>inéditas</strong> vêm primeiro.
                </p>

                {erro && <p style={{ color: '#be123c' }}>{erro}</p>}
                {!candidatas && !erro && <p style={{ color: CORES.textoFraco }}>Carregando…</p>}

                {candidatas && !candidatas.length && (
                    <p style={{ color: '#b45309', fontSize: '13px' }}>
                        Não há outra questão disponível nesta área{questao.nivel ? ` no nível ${questao.nivel}` : ''}.
                    </p>
                )}

                {candidatas && candidatas.length > 0 && (
                    <div style={{ maxHeight: '340px', overflowY: 'auto', border: `1px solid ${CORES.borda}`, borderRadius: '10px' }}>
                        {candidatas.map((c) => (
                            <div key={c.id} style={{ padding: '12px 14px', borderBottom: `1px solid #f1f5f9`, display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <div style={{ flex: 1 }}>
                                    <p style={{ margin: 0, fontSize: '13px', color: CORES.texto, lineHeight: 1.5 }}>{c.texto}</p>
                                    <div style={{ display: 'flex', gap: '8px', marginTop: '6px', alignItems: 'center' }}>
                                        {c.nivel && <BadgeStatus tom={TOM_NIVEL[c.nivel] || 'neutro'}>{c.nivel}</BadgeStatus>}
                                        <span style={{ fontSize: '11px', color: CORES.textoFraco }}>
                                            {c.vezesUsada > 0 ? `já caiu ${c.vezesUsada}x` : 'inédita'}
                                        </span>
                                    </div>
                                </div>
                                <Botao style={{ padding: '6px 14px', fontSize: '12px' }} onClick={() => onTrocar(c.id)}>
                                    Usar esta
                                </Botao>
                            </div>
                        ))}
                    </div>
                )}

                <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
                    <Botao variant="secundario" onClick={onFechar}>Cancelar</Botao>
                    {candidatas && candidatas.length > 0 && (
                        <Botao variant="secundario" icone={Shuffle} onClick={() => onTrocar(null)}>
                            Sortear por mim
                        </Botao>
                    )}
                </div>
            </div>
        </div>
    );
}

const overlay = {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 1000,
};
const painel = {
    backgroundColor: '#fff', borderRadius: '14px', padding: '22px',
    width: '100%', maxWidth: '680px', maxHeight: '85vh', overflowY: 'auto',
    boxShadow: '0 20px 50px -12px rgba(0,0,0,0.3)',
};
const btnVoltar = { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: CORES.textoSec, cursor: 'pointer', fontSize: '14px', fontWeight: 600, marginBottom: '16px', padding: 0 };
const btnIconePerigo = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '8px', borderRadius: '8px', border: '1px solid #fbb6c4', backgroundColor: '#fff', color: '#e11d48', cursor: 'pointer' };

export default PPIProvas;
